import { ProjectData, NotificationRecipient } from '../../types';
import { NotificationManager } from '../NotificationManager';
import { HelpTooltip } from '../HelpTooltip';

interface NotificationPreferencesStepProps {
  appType: 'versirent' | 'idealone';
  invalidFields?: string[];
  onFieldChange?: () => void;
  projectData?: ProjectData;
  onNotificationsChange?: (notifications: NotificationRecipient[]) => void;
}

export default function NotificationPreferencesStep({ 
  appType, 
  invalidFields = [], 
  onFieldChange,
  projectData,
  onNotificationsChange
}: NotificationPreferencesStepProps) {
  // Props are available for future use
  void invalidFields;

  // Get current notifications from projectData (new format) or create empty array
  const currentNotifications = projectData?.notifications || [];

  const handleNotificationsChange = (notifications: NotificationRecipient[]) => {
    // Call parent handler if provided
    if (onNotificationsChange) {
      onNotificationsChange(notifications);
    }
    
    // Trigger form change callback
    onFieldChange?.();
  };

  return (
    <div className="space-y-6">
      {/* Communication Preferences */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">📬 Communication Preferences</h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose how you'd like to receive updates about your project. You can add multiple email addresses or phone numbers, and enable/disable individual notifications at any time.
        </p>
        <HelpTooltip helpText="We can send you notifications via email and/or SMS when your project status changes, when we need your input, or when there are any issues that require your attention. Add as many notification recipients as you need!" />
        
        {/* Notification Manager Component */}
        <div className="mt-6">
          <NotificationManager
            notifications={currentNotifications}
            onChange={handleNotificationsChange}
          />
        </div>
      </div>

      {/* Completion Summary */}
      <div className="bg-green-50 border border-green-200 rounded-md p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              🎉 You're almost done!
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <p>
                Review your information below and submit your application when you're ready. 
                We'll start working on your {appType === 'versirent' ? 'VersiRent' : 'IdealOne'} app as soon as we receive your submission.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Notification Preferences
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                These notification preferences are optional. You can always change them later or contact us directly 
                if you have any questions about your project status.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
